import React, { useEffect, useState } from 'react';
import { getAuthToken } from '../utils/authUtils';
import './ReferralTracker.css';

const e = React.createElement;

const formatCoins = (n) =>
  typeof n === 'number' ? n.toLocaleString('en-IN') : n;

const REWARDED = 'rewarded';
const PENDING = 'pending';
const REVOKED = 'revoked';

// Somebody who has referred ten friends does not want ten cards between
// their balance and their transactions. Five is enough to show the list is
// a list; the rest are one tap away.
const INITIAL_ROWS = 5;

const titleCase = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : '';

// The whole state of a referral, in the width of a pill, named rather than
// implied: a referrer who took a code back off has to be able to see that
// is what happened, and see it go back to applied if it is entered again.
//
// What became of the referral outranks what became of the booking, so a
// credited referral still reads as credited if the booking is cancelled
// afterwards. The money moved. A settled one outranks it for the same
// reason: the coins are coming whatever the booking does next, so saying
// 'Cancelled' over the top of that would be the one untrue thing here.
const chipFor = (referral) => {
  if (referral.status === REVOKED) {
    return { text: 'Removed', variant: 'muted' };
  }
  if (referral.status === REWARDED) {
    return { text: 'Credited', variant: 'done' };
  }
  // The booking held its confirmation: nothing is in doubt any more and
  // only the payout window is left, which the step below dates.
  if (referral.is_owed) {
    return { text: 'Confirmed', variant: 'done' };
  }
  if (referral.is_cancelled) {
    return { text: 'Cancelled', variant: 'muted' };
  }
  if (referral.status === PENDING) {
    return { text: 'Applied', variant: 'pending' };
  }
  return { text: titleCase(referral.status), variant: 'muted' };
};

// What a step is waiting on, when it has not happened. Only the last step
// can be outstanding, and why it is outstanding is the thing the referrer
// came to find out.
//
// A reward still coming is quoted rather than left blank: the referrer's
// question is how much, and the API prices it through the same function
// that will do the crediting. The star is what carries the caveat, and
// the line under the steps is what it points at, since the booking the
// figure is a percentage of is still being quoted.
//
// With the date it is expected, where there is one. A reward that lands
// two days after the booking sticks is worth waiting for; a reward with
// no date on it reads like one that is not coming.
const pendingValue = (referral, isLast) => {
  if (!isLast) return 'Waiting';
  if (referral.status === REVOKED) return 'Removed';
  const coins =
    referral.pending_coins != null
      ? `${formatCoins(referral.pending_coins)} coins*`
      : null;
  // A due date means the reward is already earned, so it outranks the
  // booking falling over afterwards: the coins land on that date whatever
  // the booking does, and telling the referrer 'Cancelled' instead would
  // be the one thing on this card that is not true.
  if (referral.reward_due_at) {
    const due = `by ${referral.reward_due_at}`;
    return coins ? `${coins} · ${due}` : due;
  }
  if (referral.is_cancelled) return 'Cancelled';
  return coins || 'Waiting';
};

// The figure the referrer came for, put on the row itself so ten referrals
// can be read without opening any of them. Says the same thing the last
// step would: what landed, once it has, and what is quoted until then.
//
// Nothing where there is no figure left to state. A referral taken back off
// is not owed anything, and a dead booking with no due date has a quote that
// is no longer going anywhere - in both cases a number on the row would be
// read as money coming.
const rowAmount = (referral) => {
  if (referral.status === REVOKED) return null;
  if (referral.reward_coins != null) {
    return { text: `+${formatCoins(referral.reward_coins)}`, variant: 'done' };
  }
  if (referral.pending_coins == null) return null;
  if (referral.is_cancelled && !referral.reward_due_at) return null;
  // Carries the star up from the detail: the quote moves with the booking,
  // and the line that says so is one tap away under the steps. A confirmed
  // reward drops that line, so the star goes with it rather than pointing
  // at a footnote that is no longer there.
  const star = referral.is_owed ? '' : '*';
  return {
    text: `${formatCoins(referral.pending_coins)}${star}`,
    variant: 'pending',
  };
};

// The arrow, drawn rather than typed. The ⌄ glyph it replaces landed a few
// pixels tall in a pale grey, so the row gave no sign it opened at all.
// Sized, circled and rotated instead: at rest it points down and reads as
// a control, and turning the whole disc is what says the row is open.
const caret = (isOpen) =>
  e(
    'span',
    {
      className: `wallet-ref-caret${isOpen ? ' open' : ''}`,
      'aria-hidden': 'true',
    },
    e(
      'svg',
      { viewBox: '0 0 24 24', width: 13, height: 13 },
      e('path', {
        d: 'M6 9.5l6 6 6-6',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 3,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      })
    )
  );

// Where the dates live. The row above already carries the figure, so this
// is what a referrer opens for one referral at a time rather than ten at
// once: when each step happened, and when the last one is due.
const detail = (referral) => {
  const steps = referral.steps || [];
  return e(
    'div',
    { className: 'wallet-ref-detail' },
    steps.map((step, i) => {
      const isLast = i === steps.length - 1;
      let value = pendingValue(referral, isLast);
      if (step.is_done) {
        value = step.date || 'Done';
        // The row states the figure; here it is paired with the date it
        // landed on, which the row has no room for.
        if (isLast && referral.reward_coins != null) {
          value = `+${formatCoins(referral.reward_coins)} · ${value}`;
        }
      }
      return e(
        'div',
        { className: 'wallet-ref-detail-row', key: step.key ?? i },
        e('span', { className: 'wallet-ref-detail-label' }, step.label),
        e(
          'span',
          {
            className: `wallet-ref-detail-value${
              step.is_done ? ' done' : ''
            }`,
          },
          value
        )
      );
    }),
    // Sits under the figure it qualifies, and only where there is one: a
    // quoted reward has to say out loud that it moves with the booking,
    // and a credited one has nothing left to qualify. Opens with the same
    // star the figure carries, so the two read as one sentence.
    //
    // Dropped once the reward is confirmed: at that point the referrer has
    // been told the reward is settled, and a caveat under it reads as the
    // figure still being in doubt.
    referral.pending_coins != null && !referral.is_owed
      ? e(
          'p',
          { className: 'wallet-ref-disclaimer' },
          '* Reward amount is subject to the final price of the quotation.'
        )
      : null
  );
};

/**
 * The referrer's own referrals, as a list they can scan.
 *
 * Coins are credited once the referee's booking is confirmed, which can be
 * weeks after the code was used, so a referrer who is only shown a balance
 * has no way to tell a reward that is coming from one that never will. This
 * is that answer, and it quotes the reward while it is still coming
 * (``pending_coins``), starred and footnoted: the figure is a percentage of
 * a booking that is still being quoted, so it moves until the credit lands.
 *
 * One row each, not one card each. Somebody with ten referrals is the case
 * that decides this layout, and for them the questions are which friends
 * booked and what landed, both of which fit on a line. The state is named
 * on the row in words rather than drawn, and the dates sit behind a tap,
 * one referral open at a time.
 *
 * Loads itself rather than riding on the wallet's own fetch: a referral read
 * that fails has to leave the wallet standing, and nothing on this page can
 * change a referral.
 *
 * Renders nothing at all for someone with no referrals. The refer-and-earn
 * tile above it is already the invitation.
 */
export const ReferralTracker = ({ getAPIUrl }) => {
  const [referrals, setReferrals] = useState([]);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchReferrals = async () => {
      try {
        const res = await fetch(getAPIUrl('/api/ads/referrals/'), {
          method: 'GET',
          headers: { Authorization: getAuthToken() },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setReferrals(Array.isArray(data.referrals) ? data.referrals : []);
        setCoinsEarned(data.total_coins_earned ?? 0);
      } catch (err) {
        // The section stays hidden. The wallet itself is unaffected.
      }
    };
    fetchReferrals();
    return () => {
      cancelled = true;
    };
  }, [getAPIUrl]);

  if (referrals.length === 0) return null;

  const shown = showAll ? referrals : referrals.slice(0, INITIAL_ROWS);
  const hidden = referrals.length - shown.length;

  return e(
    'div',
    { className: 'wallet-ref-section' },
    e(
      'div',
      { className: 'wallet-txn-header' },
      e('span', null, 'Your Referrals'),
      coinsEarned > 0
        ? e(
            'span',
            { className: 'wallet-ref-earned' },
            `${formatCoins(coinsEarned)} coins earned`
          )
        : null
    ),
    // The condition, not just the timing. The old line dated the payout but
    // never said it was conditional, so a quoted figure against an applied
    // referral read as money already earned - and a referrer whose friend
    // never confirms had no way to know the coins were never coming.
    e(
      'p',
      { className: 'wallet-ref-note' },
      e(
        'span',
        { className: 'wallet-ref-note-icon', 'aria-hidden': 'true' },
        '🎁'
      ),
      e(
        'span',
        null,
        'Coins are credited ',
        e(
          'strong',
          null,
          'only if your friend’s booking is confirmed'
        ),
        ' — they land within 2 working days of that confirmation.'
      )
    ),
    shown.map((referral, i) => {
      const chip = chipFor(referral);
      const amount = rowAmount(referral);
      const id = referral.id ?? i;
      const isOpen = openId === id;
      return e(
        'div',
        {
          className: `wallet-ref-item${
            chip.variant === 'muted' ? ' dim' : ''
          }`,
          key: id,
        },
        e(
          'button',
          {
            className: 'wallet-ref-row',
            type: 'button',
            // One open at a time, or the list grows back into the pile of
            // cards this replaced.
            onClick: () => setOpenId(isOpen ? null : id),
            'aria-expanded': isOpen,
          },
          e(
            'span',
            { className: 'wallet-ref-avatar', 'aria-hidden': 'true' },
            (referral.referee_name || '?').charAt(0).toUpperCase()
          ),
          e(
            'span',
            { className: 'wallet-ref-who' },
            e(
              'span',
              { className: 'wallet-ref-name' },
              referral.referee_name || 'Your friend'
            ),
            referral.referee_phone
              ? e(
                  'span',
                  { className: 'wallet-ref-phone' },
                  referral.referee_phone
                )
              : null
          ),
          // Reads left to right as one sentence: who, how much, where it
          // has got to. The name is what gives way when the row runs out
          // of width, since a truncated name is still recognisable and a
          // truncated figure is not a figure.
          amount
            ? e(
                'span',
                { className: `wallet-ref-amount ${amount.variant}` },
                amount.text
              )
            : null,
          e(
            'span',
            { className: `wallet-ref-chip ${chip.variant}` },
            chip.text
          ),
          caret(isOpen)
        ),
        isOpen ? detail(referral) : null
      );
    }),
    hidden > 0
      ? e(
          'button',
          {
            className: 'wallet-ref-more',
            type: 'button',
            onClick: () => setShowAll(true),
          },
          `View all ${referrals.length} referrals`
        )
      : null
  );
};
